import { useState } from "react";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender";
import { AavoButton } from "src/components/common/buttons/AavoButton";
import { FileHandlePreviewView } from "src/components/views/documents/preview/FileHandlePreviewView.tsx";
import i18n from "i18next";
import { AavoActionBar } from "src/components/common/actionBar/AavoActionBar.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { faCommentNodes, faRefresh } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { ConfiguratorLogApi } from "src/api/generated/erp/configurator/api/configuratorLogApi.ts";
import { AavoMarkdown } from "src/components/common/AavoMarkdown.tsx";
import { Typography } from "@mui/material";

export interface ConfiguratorLogViewBaseProps {
	fetchLogPathBase64: () => Promise<string>;
	fetchOnLoad?: boolean;
	hideRefreshButton?: boolean;
}

export const ConfiguratorLogView = ({
	fetchOnLoad = false,
	hideRefreshButton = false,
	fetchLogPathBase64,
}: ConfiguratorLogViewBaseProps) => {
	const [initialFetchRequested, setInitialFetchRequested] = useState(fetchOnLoad);
	const [aiAnalysis, setAiAnalysis] = useState<string | undefined>(undefined);

	if (!initialFetchRequested)
		return (
			<AavoButton
				label={i18n.t("load")}
				onClick={() => setInitialFetchRequested(true)}
				variant={"contained"}
				sx={{
					margin: "auto",
				}}
			/>
		);

	return (
		<AsyncFetchRender
			fetch={async () => {
				const logPathBase64 = await fetchLogPathBase64();
				const logFileHandle = await ConfiguratorLogApi.getConfiguratorLogFileByPath({ logPathBase64 });
				return {
					logPathBase64,
					logFileHandle,
				};
			}}
			content={({ logPathBase64, logFileHandle }, refresh) => {
				return (
					<VerticalBox
						sx={{
							flex: 1,
							gap: 1,
							position: "relative",
							overflow: "auto",
							padding: 1,
							"& *": {
								minHeight: "auto",
							},
						}}
					>
						{!hideRefreshButton && (
							<AavoActionBar
								sx={{
									alignSelf: "stretch",
									borderBottom: "1px solid",
									borderColor: "divider",
								}}
							>
								<AsyncButton icon={faRefresh} color={"primary"} onClick={() => refresh()} />
							</AavoActionBar>
						)}
						{aiAnalysis == null ?
							<AsyncButton
								label={i18n.t("analyze_with_ai")}
								icon={faCommentNodes}
								variant={"contained"}
								size={"small"}
								sx={{
									alignSelf: "flex-start",
								}}
								onClick={async () => {
									const response = await ConfiguratorLogApi.analyzeLogWithAi({
										logPathBase64: logPathBase64,
									});
									setAiAnalysis(response);
								}}
							/>
						:	<VerticalBox
								sx={{
									minHeight: "auto",
									flex: "1 0 100%",
								}}
							>
								<AavoMarkdown>{aiAnalysis}</AavoMarkdown>
							</VerticalBox>
						}
						<Typography variant={"h6"} sx={{ padding: 1 }}>
							{i18n.t("log_file")}:
						</Typography>
						<VerticalBox
							sx={{
								flex: "1 0 100%",
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 1,
							}}
						>
							<FileHandlePreviewView fileHandle={logFileHandle} />
						</VerticalBox>
					</VerticalBox>
				);
			}}
		/>
	);
};
